/**
 * frontend-react/src/services/reviewHistoryService.ts
 * 全文審閱歷史記錄和分享功能 API 服務
 */

import axios from 'axios';
import { getApiBaseUrl } from '../config/api';

const API_BASE = `${getApiBaseUrl()}/api/v1/review-history`;

export interface ReviewHistoryItem {
  id: number;
  sharing_id?: number; // 收件方移除分享時使用
  original_filename: string;
  file_type?: string;
  title?: string;
  risk_summary: { high: number; medium: number; low: number };
  total_issues: number;
  created_at: string;
  notes?: string;
  is_shared: boolean;
  has_file_data?: boolean;
  shared_by?: number;
  shared_at?: string;
  share_message?: string;
  owner_name?: string;
  owner_department?: string;
  sharer_name?: string;
  rule_set_id?: number | null;
  rule_set_name?: string | null;
}

export interface ReviewHistoryDetail extends ReviewHistoryItem {
  file_size?: number;
  file_content: string;
  ai_analyses: any[];
  user_final_analyses?: any[];
  updated_at: string;
  has_file_data?: boolean; // 新增：是否有原始檔案
}

export interface CompanyUser {
  id: number;
  name: string;
  email: string;
  department_id?: number;
}

export type HistoryRecordType = 'review' | 'comparison';

export interface SaveReviewRequest {
  original_filename: string;
  file_type?: string;
  file_size?: number;
  file_content: string;
  file_data_base64?: string; // 新增：原始檔案的 Base64 編碼
  ai_analyses: any[];
  user_final_analyses?: any[];
  risk_summary: { high: number; medium: number; low: number };
  title?: string;
  notes?: string;
  rule_set_id?: number | null;
  rule_set_name?: string | null;
  record_type?: HistoryRecordType;
}

export interface ShareRequest {
  record_type: 'review' | 'comparison';
  record_id: number;
  shared_to: number[];
  message?: string | undefined;
}

/**
 * 保存全文審閱歷史記錄
 */
export const saveReviewHistory = async (
  request: SaveReviewRequest
): Promise<{ ok: boolean; record_id: number }> => {
  const token = localStorage.getItem('auth_token');
  const response = await axios.post(`${API_BASE}/save`, request, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  return response.data;
};

export type ViewerRole = 'admin' | 'department_manager' | 'user';

export interface ReviewHistoryResponse {
  own_records: ReviewHistoryItem[];
  department_records: ReviewHistoryItem[];
  company_records: ReviewHistoryItem[];
  shared_records: ReviewHistoryItem[];
  viewer_role: ViewerRole;
}

/**
 * 獲取歷史記錄列表
 */
export const getReviewHistory = async (
  recordType: HistoryRecordType = 'review'
): Promise<ReviewHistoryResponse> => {
  const token = localStorage.getItem('auth_token');
  const response = await axios.get(`${API_BASE}/history`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: { record_type: recordType },
  });
  return response.data;
};

/**
 * 獲取歷史記錄詳情
 */
export const getReviewDetail = async (recordId: number): Promise<ReviewHistoryDetail> => {
  const token = localStorage.getItem('auth_token');
  const response = await axios.get(`${API_BASE}/${recordId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * 刪除歷史記錄
 */
export const deleteReviewHistory = async (recordId: number): Promise<{ ok: boolean }> => {
  const token = localStorage.getItem('auth_token');
  const response = await axios.delete(`${API_BASE}/${recordId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * 批次刪除歷史記錄
 */
export const batchDeleteReviewHistory = async (
  ids: number[]
): Promise<{ ok: boolean; deleted_count: number; failed_ids: number[] }> => {
  const token = localStorage.getItem('auth_token');
  const response = await axios.delete(`${API_BASE}/batch`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: { ids },
  });
  return response.data;
};

/**
 * 分享記錄給其他用戶
 */
export const shareRecord = async (
  request: ShareRequest
): Promise<{ ok: boolean; sharing_ids: number[] }> => {
  const token = localStorage.getItem('auth_token');
  const response = await axios.post(`${API_BASE}/share`, request, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  return response.data;
};

/**
 * 收件方移除單筆分享
 */
export const dismissSharing = async (sharingId: number): Promise<{ ok: boolean }> => {
  const token = localStorage.getItem('auth_token');
  const response = await axios.delete(`${API_BASE}/share/dismiss/${sharingId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

/**
 * 收件方批次移除分享
 */
export const batchDismissSharing = async (
  sharingIds: number[]
): Promise<{ ok: boolean; dismissed_count: number; failed_ids: number[] }> => {
  const token = localStorage.getItem('auth_token');
  const response = await axios.delete(`${API_BASE}/share/dismiss/batch`, {
    headers: { Authorization: `Bearer ${token}` },
    data: { sharing_ids: sharingIds },
  });
  return response.data;
};

/**
 * 撤回分享
 */
export const revokeSharing = async (sharingId: number): Promise<{ ok: boolean }> => {
  const token = localStorage.getItem('auth_token');
  const response = await axios.delete(`${API_BASE}/share/${sharingId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * 獲取同公司用戶列表（用於選擇分享對象）
 */
export const getCompanyUsers = async (): Promise<{ users: CompanyUser[] }> => {
  const token = localStorage.getItem('auth_token');
  const response = await axios.get(`${API_BASE}/company/users`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * 下載原始 PDF/DOCX 檔案
 */
export const downloadOriginalFile = async (recordId: number, filename: string): Promise<void> => {
  const token = localStorage.getItem('auth_token');
  const response = await axios.get(`${API_BASE}/${recordId}/download`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    responseType: 'blob',
  });

  // 創建下載連結
  const url = globalThis.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  globalThis.URL.revokeObjectURL(url);
};
